import React, { SyntheticEvent, useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../Redux/store';
import { setAuth } from '../Redux/authSlice';
import { translations } from '../utils/translations';

const Register: React.FC = () => {
    const [first_name, setFirstName] = useState('');
    const [last_name, setLastName] = useState('');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [passwordConfirm, setPasswordConfirm] = useState('');
    const [userType, setUserType] = useState('admin');  // Default to 'admin'
    const [branch, setBranch] = useState<number | null>(null);
    const [branchName, setBranchName] = useState('');
    const [redirect, setRedirect] = useState(false);
    const [formInvalid, setFormInvalid] = useState(false);
    const dispatch = useDispatch();
    const [userRole, setUserRole] = useState<string>('');
    const auth = useSelector((state: RootState) => state.auth.value);
    const language = useSelector((state: RootState) => state.language.language);
    const t = translations[language as keyof typeof translations];

    const submit = async (e: SyntheticEvent) => {
        e.preventDefault();

        if (!first_name || !last_name || !email || !password || !passwordConfirm) {
            setFormInvalid(true);
            return;
        }

        if (password !== passwordConfirm) {
            alert('Password and password confirmation do not match.');
            return;
        }
        try {
            await axios.post('https://api.sominnovations.xyz/users/register', {
                first_name,
                last_name,
                email,
                password,
                password_confirm: passwordConfirm,
                role: userType,
                branch,
                branch_name: branchName
            });

            setRedirect(true);
        } catch (error) {
            console.error('Error registering user:', error);
            alert('An error occurred while registering the user.');
        }
    }

    useEffect(() => {
        const fetchData = async () => {
            try {
                const token = getCookie('token');
                if (!token) {
                    dispatch(setAuth(false));
                    return;
                }
                axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
                const { data } = await axios.get('https://api.sominnovations.xyz/users/user');
                dispatch(setAuth(true));
            } catch (e) {
                dispatch(setAuth(false));
            }
        };
        const fetchUserRole = async () => {
            try {
                const { data } = await axios.get('https://api.sominnovations.xyz/users/user');
                setUserRole(data.user.role);
            } catch (error) {
                console.error('Failed to fetch user role:', error);
            } finally {
            }
        };
        fetchData();
        fetchUserRole()
    }, [dispatch]);

    const getCookie = (name: string) => {
        const cookieName = name + "=";
        const decodedCookie = decodeURIComponent(document.cookie);
        const cookieArray = decodedCookie.split(';');
        for (let i = 0; i < cookieArray.length; i++) {
            let cookie = cookieArray[i];
            while (cookie.charAt(0) === ' ') {
                cookie = cookie.substring(1);
            }
            if (cookie.indexOf(cookieName) === 0) {
                return cookie.substring(cookieName.length, cookie.length);
            }
        }
        return '';
    }

    if (redirect) {
        return <Navigate to="/users" />;
    }

    return (
        <div className="container">
            {auth ? (
                <div className="row justify-content-center">
                    {userRole !== 'accountant' ? (
                        <div className="col-md-6" style={{ margin: "10px auto" }}>
                            <form onSubmit={submit} className="mt-5">
                                <h1 className="h3 mb-3 fw-normal text-center">{t.pleaseRegister}</h1>
                                {formInvalid && (
                                    <div className="alert alert-danger" role="alert">
                                        Please fill in all the required fields.
                                    </div>
                                )}
                                <div className="mb-3">
                                    <label htmlFor="userType" className="form-label">{t.userType}:</label>
                                    <select
                                        className="form-select"
                                        id="userType"
                                        value={userType}
                                        onChange={(e) => setUserType(e.target.value)}
                                    >
                                        <option value="admin">Admin</option>
                                        <option value="accountant">Accountant</option>
                                    </select>
                                </div>

                                <div className="mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="firstName"
                                            placeholder={t.firstName}
                                            value={first_name}
                                            onChange={e => setFirstName(e.target.value)}
                                            required
                                        />
                                        <label htmlFor="firstName">{t.firstName}</label>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="text"
                                            className="form-control"
                                            id="lastName"
                                            placeholder={t.lastName}
                                            value={last_name}
                                            onChange={e => setLastName(e.target.value)}
                                            required
                                        />
                                        <label htmlFor="lastName">{t.lastName}</label>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="email"
                                            className="form-control"
                                            id="email"
                                            placeholder="name@example.com"
                                            value={email}
                                            onChange={e => setEmail(e.target.value)}
                                            required
                                        />
                                        <label htmlFor="email">{t.email}</label>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="password"
                                            placeholder={t.password}
                                            value={password}
                                            onChange={e => setPassword(e.target.value)}
                                            required
                                        />
                                        <label htmlFor="password">{t.password}</label>
                                    </div>
                                </div>
                                <div className="mb-3">
                                    <div className="form-floating">
                                        <input
                                            type="password"
                                            className="form-control"
                                            id="passwordConfirm"
                                            placeholder={t.confirmPassword}
                                            value={passwordConfirm}
                                            onChange={e => setPasswordConfirm(e.target.value)}
                                            required
                                        />
                                        <label htmlFor="passwordConfirm">{t.confirmPassword}</label>
                                    </div>
                                </div>

                                {/* Branch and Branch Name Fields */}
                                <div className="mb-3">
                                    {/* <div className="form-floating">
                                    <input 
                                        type="number" 
                                        className="form-control" 
                                        id="branch" 
                                        placeholder="Branch"
                                        value={branch || ''} // If branch is null, show an empty string
                                        onChange={e => setBranch(Number(e.target.value))}
                                    />
                                    <label htmlFor="branch">Branch</label>
                                </div> */}
                                </div>
                                <div className="mb-3">
                                </div>
                                <button className="w-100 btn btn-lg btn-primary mt-3" type="submit">{t.submit}</button>
                            </form>
                        </div>
                    ) : (
                        <div>
                            <h4 style={{ textAlign: "center", marginTop: "20px" }}>{t.unauthorizedAccess}</h4>
                        </div>
                    )}
                </div>
            ) : (
                <div>
                    <h4 style={{ textAlign: "center", marginTop: "20px" }}>You are not authenticated</h4>
                </div>
            )}
        </div>
    );
}

export default Register;
